import { useLocales } from "@/locales";
import { CommonWrap } from "@/PageComponents/common";
import ContentWrap from "@/DocLayout/Content/Index";
import { Box, Link } from "@mui/material";
import { Trans } from "react-i18next";
import ShowTable from "@/DocLayout/Table/index";
import { ADMIN_DEV_URL } from "@/config";
import CodeSnippet from "@/components/CodeSnippet";
import RespTempImg from "@/assets/img/webhook-use/resp-temp.png";
import Image from "@/components/image";
import { useContext } from "react";
import { ChooseCodeLanguageContext } from "@/components/ChooseCodeLanguage/ChooseCodeLanguage";

export default function WebhookNotificationSteps() {
  const { translate, currentLang } = useLocales();
  const { language } = useContext(ChooseCodeLanguageContext);
  const codeMap: { [key: string]: any } = {
    javascript: {
      temp_code: `const express = require('express');
const crypto = require('crypto');
const app = express();
function verifySignature(content, signature, app_id, app_secret, timestamp) {
  let sign_text = \`\${app_id}\${timestamp}\${content}\`;
  let server_sign = crypto.createHmac('sha256', app_secret).update(sign_text).digest('hex');
  return signature === server_sign;
}
app.use(express.text());
app.post('/webhook', (req, res) => {
    const app_id = 'your_app_id';
    const app_secret = 'your_app_secret';
    const timestamp = req.header('Timestamp');
    const sign = req.header('Sign');
    const sign_text = req.body;
    if (verifySignature(sign_text, sign, app_id, app_secret, timestamp)) {
        res.send('success');
    } else {
        res.status(401).send('Invalid signature');
    }
});
app.listen(3000, () => {
    console.log('Server is running on port 3000');
});`,
      resp_example_code: ``,
    },
    python: {
      temp_code: `# Python FastAPI
import hashlib
import hmac
import json
import binascii
from fastapi import FastAPI, HTTPException, Request
from pydantic import BaseModel
from fastapi.responses import PlainTextResponse

app = FastAPI()

def verify_signature(content: str, signature: str, app_id: str, app_secret: str, timestamp: str):
    if content == '':
        sign_text = f"{app_id}{timestamp}"
    else:
        sign_text = f"{app_id}{timestamp}{content}"
    h = hmac.new(app_secret.encode('utf-8'), sign_text.encode('utf-8'), hashlib.sha256)
    server_sign = binascii.hexlify(h.digest()).decode('utf-8')
    if signature == server_sign:
        return True
    else:
        return False
        
@app.post("/webhook", response_class=PlainTextResponse)
async def process_webhook(request: Request):
    app_id = "your_app_id"
    app_secret = "your_app_secret"
    timestamp = request.headers.get("Timestamp")
    sign = request.headers.get("Sign")
    sign_text = await request.body()
    if verify_signature(content=sign_text.decode('utf-8'), signature=sign, app_id=app_id, app_secret=app_secret,
                        timestamp=timestamp):
        return "success"
    else:
        raise HTTPException(status_code=401, detail="Invalid signature")`,
      resp_example_code: ``,
    },
    go: {
      temp_code: `package main
import (
  "crypto/hmac"
  "crypto/sha256"
  "encoding/hex"
  "github.com/gin-gonic/gin"
  "io/ioutil"
  "net/http"
)
func verifySignature(content, signature, appID, appSecret, timestamp string) bool {
  signText := appID + timestamp + content
  h := hmac.New(sha256.New, []byte(appSecret))
  h.Write([]byte(signText))
  serverSign := hex.EncodeToString(h.Sum(nil))
  return signature == serverSign
}
func processWebhook(c *gin.Context) {
  appID := "your_app_id"
  appSecret := "your_app_secret"
  timestamp := c.GetHeader("Timestamp")
  sign := c.GetHeader("Sign")
  signText, _ := ioutil.ReadAll(c.Request.Body)
  if verifySignature(string(signText), sign, appID, appSecret, timestamp) {
    c.String(http.StatusOK, "success")
  } else {
    c.AbortWithStatusJSON(http.StatusUnauthorized, gin.H{"error": "Invalid signature"})
  }
}
func main() {
  router := gin.Default()
  router.POST("/webhook", processWebhook)
  router.Run(":3000")
}`,
      resp_example_code: ``,
    },
    php: {
      temp_code: `use PsrHttpMessageResponseInterface as Response;
use PsrHttpMessageServerRequestInterface as Request;
use SlimFactoryAppFactory;
require __DIR__ . '/vendor/autoload.php';
function verifySignature($content, $signature, $appID, $appSecret, $timestamp)
{
    $signText = $appID . $timestamp . $content;
    $serverSign = hash_hmac('sha256', $signText, $appSecret);
    return $signature === $serverSign;
}
$app = AppFactory::create();
$app->addBodyParsingMiddleware();
$app->post('/webhook', function (Request $request, Response $response) {
    $appID = 'your_app_id';
    $appSecret = 'your_app_secret';
    $timestamp = $request->getHeaderLine('Timestamp');
    $sign = $request->getHeaderLine('Sign');
    $signText = $request->getBody()->getContents();
    if (verifySignature($signText, $sign, $appID, $appSecret, $timestamp)) {
        $response->getBody()->write('success');
    } else {
        $response->getBody()->write('Invalid signature');
        $response = $response->withStatus(401);
    }
    return $response;
});
$app->run();`,
      resp_example_code: ``,
    },
    java: {
      temp_code: `import org.springframework.boot.SpringApplication;
import org.springframework.boot.autoconfigure.SpringBootApplication;
import org.springframework.http.HttpStatus;
import org.springframework.http.ResponseEntity;
import org.springframework.web.bind.annotation.PostMapping;
import org.springframework.web.bind.annotation.RequestBody;
import org.springframework.web.bind.annotation.RequestHeader;
import org.springframework.web.bind.annotation.RestController;
import javax.crypto.Mac;
import javax.crypto.spec.SecretKeySpec;
import javax.servlet.http.HttpServletRequest;
import java.nio.charset.StandardCharsets;
import java.security.InvalidKeyException;
import java.security.NoSuchAlgorithmException;
@SpringBootApplication
@RestController
public class WebhookApplication {
public static void main(String[] args) {
    SpringApplication.run(WebhookApplication.class, args);
}
@PostMapping("/webhook")
public ResponseEntity<String> processWebhook(@RequestBody String content,
    @RequestHeader("Timestamp") String timestamp,
    @RequestHeader("Sign") String signature,
    HttpServletRequest request) {
    String appID = "your_app_id";
    String appSecret = "your_app_secret";
    String signText = appID + timestamp + content;
    if (verifySignature(signText, signature, appSecret)) {
        return ResponseEntity.ok("success");
    } else {
        return ResponseEntity.status(HttpStatus.UNAUTHORIZED).body("Invalid signature");
    }
}
private boolean verifySignature(String signText, String signature, String appSecret) {
    try {
        Mac sha256Hmac = Mac.getInstance("HmacSHA256");
        SecretKeySpec secretKey = new SecretKeySpec(appSecret.getBytes(StandardCharsets.UTF_8), "HmacSHA256");
        sha256Hmac.init(secretKey);
        byte[] hmacBytes = sha256Hmac.doFinal(signText.getBytes(StandardCharsets.UTF_8));
        String serverSign = bytesToHexString(hmacBytes);
        return signature.equals(serverSign);
    } catch (NoSuchAlgorithmException | InvalidKeyException e) {
        e.printStackTrace();
        return false;
    }
}
private String bytesToHexString(byte[] bytes) {
    StringBuilder result = new StringBuilder();
    for (byte b : bytes) {
        result.append(String.format("%02x", b));
    }
    return result.toString();
}
}`,
      resp_example_code: ``,
    },
  };

  return (
    <ContentWrap
      showBorderBottom={false}
      leftChildrenSx={{
        paddingTop: "1px",
        paddingBottom: "1px",
      }}
      leftChildren={
        <CommonWrap>
          <>
            <Box
              id="how-to-handle-incoming-webhooks"
              component={"div"}
              className="title_0"
            >
              {translate("webhook_use.notifiy_step_title")}
            </Box>

            <Box component={"div"} className="title_1">
              {translate("webhook_use.notifiy_step_list1_title")}
            </Box>

            <Box
              sx={{
                ul: {
                  paddingLeft: "2em",
                },
                li: {
                  "::marker": {
                    textIndent: "2em",
                  },
                },
              }}
            >
              <ul>
                <li>
                  <Box component={"div"} className="desc">
                    {translate(`webhook_use.notifiy_step_list1_desc1`)}
                  </Box>
                  <ShowTable
                    tableData={[
                      {
                        name: "Appid",
                        type: "String",
                        is_: `${translate("common.params_is")}`,
                        desc: (
                          <Trans
                            i18nKey={
                              "quick_start.title5_desc1_table_app_id_desc"
                            }
                            t={translate as any}
                          >
                            Just a placeholder
                            <Link
                              href={ADMIN_DEV_URL}
                              target="_blank"
                              className="link"
                              underline="always"
                            >
                              Just a placeholder
                            </Link>
                          </Trans>
                        ),
                      },
                      {
                        name: "Timestamp",
                        type: "String",
                        is_: `${translate("common.params_is")}`,
                        desc: `${translate(
                          "quick_start.title5_desc1_table_timestamp_desc"
                        )}`,
                      },
                      {
                        name: "Sign",
                        type: "String",
                        is_: `${translate("common.params_is")}`,
                        desc: `${translate(
                          "quick_start.title5_desc1_table_sgin_desc"
                        )}`,
                      },
                    ]}
                  />
                </li>
                <li>
                  <Box component={"div"} className="desc" mt={1}>
                    <Trans
                      i18nKey={"webhook_use.notifiy_step_list1_desc2"}
                      t={translate as any}
                    >
                      Just a placeholder
                      <a
                        href={"#api-authentication-and-specifications"}
                        className="link"
                      >
                        Just a placeholder
                      </a>
                    </Trans>
                  </Box>
                </li>
                <li>
                  <Box component={"div"} className="desc">
                    {translate(`webhook_use.notifiy_step_list1_desc3`)}
                  </Box>
                </li>
                <li>
                  <Box component={"div"} className="desc">
                    {translate(`webhook_use.notifiy_step_list1_desc4`)}
                  </Box>
                </li>
                <li>
                  <Box component={"div"} className="desc">
                    {translate(`webhook_use.notifiy_step_list1_desc5`)}
                  </Box>
                </li>
              </ul>
            </Box>

            {currentLang.value === "en" ? (
              <Box component={"div"} className="desc">
                <Trans
                  i18nKey={"webhook_use.notifiy_step_list2_title"}
                  t={translate as any}
                >
                  s<strong></strong>
                  <Link underline="always"></Link>
                </Trans>
              </Box>
            ) : (
              <Box component={"div"} className="title_1">
                {translate("webhook_use.notifiy_step_list2_title")}
              </Box>
            )}

            {currentLang.value === "en" ? (
              <Box component={"div"} className="desc">
                <Trans
                  i18nKey={"webhook_use.notifiy_step_list3_title"}
                  t={translate as any}
                >
                  s<strong></strong>
                </Trans>
              </Box>
            ) : (
              <Box component={"div"} className="title_1">
                {translate("webhook_use.notifiy_step_list3_title")}
              </Box>
            )}

            <Image src={RespTempImg} />
          </>
        </CommonWrap>
      }
      rightChildren={
        <>
          <CodeSnippet
            title={translate("common.sign_example")}
            code={codeMap[language].temp_code}
          />
          {/* <Box mb={3} /> */}
          {/* <CodeSnippet
            title={translate("common.resp_example")}
            code={codeMap[language].resp_example_code}
          /> */}
        </>
      }
    />
  );
}
